"use client";
import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { AuthContext } from "../context/AuthContext";

function ProductCard({ image, title, id, onProductClick }) {
  const [quantity, setQuantity] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState("");

  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { user } = useContext(AuthContext);

  // Check subscription: either from context or localStorage
  const isSubscribed =
    !!user?.subscription?.id || !!localStorage.getItem("sub_id");

  // Check if product is already in wishlist
  const isWishlisted = wishlist.some((item) => item.id === id);

  const handleWishlistClick = (e) => {
    e.stopPropagation();

    const product = {
      id: id,
      title: title,
      image: image,
    };

    if (isWishlisted) {
      removeFromWishlist(id);
    } else {
      addToWishlist(product);
    }
  };

  // Lazy load the product image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSrc(image);
      setImageLoaded(true);
    };
    img.src = image;
  }, [image]);

  const incrementQuantity = () =>
    setQuantity((prev) => Math.min(prev + 1, 7));
  const decrementQuantity = () =>
    setQuantity((prev) => Math.max(prev - 1, 1));

  const handleCardClick = (e) => {
    if (e.target.closest("button")) return;
    if (onProductClick && id) onProductClick(id);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!isSubscribed) return alert("You need a subscription to add products to cart");
    addToCart({ id, name: title, image }, quantity);
  };

  return (
  <motion.article
  className="overflow-hidden relative bg-white rounded-3xl border border-[1.5px] border-black border-opacity-10 
  shadow-[0_8px_34px_rgba(0,0,0,0.10)] flex flex-col cursor-pointer 
  w-[90%] sm:w-[260px] md:w-[280px] lg:w-[300px] xl:w-[320px] h-[450px] mx-auto"
  whileHover={{ y: -5 }}
  transition={{ duration: 0.3 }}
  onClick={handleCardClick}
  role="button"
  tabIndex={0}
  onKeyDown={(e) => {
    if (e.key === "Enter" || e.key === " ") handleCardClick(e);
  }}
  aria-label={`View details for ${title}`}
>

      <div className="relative overflow-hidden rounded-[22px_22px_0_0] h-[298px] flex-shrink-0">
        {!imageLoaded && (
          <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
            <span className="text-gray-500">Loading...</span>
          </div>
        )}

        {imageLoaded && (
          <motion.img
            src={imageSrc}
            alt={title}
            className="object-cover w-full h-full aspect-[1/1]"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          />
        )}

        <div className="flex absolute gap-2.5 right-[13px] top-[13px]">
          <motion.button
            className="relative h-[34px] w-[34px] bg-white rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-md"
            onClick={(e) => e.stopPropagation()}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Mark as premium"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M16.6196 21.2389H7.38043C7.00163 21.2389 6.6875 20.9248 6.6875 20.546C6.6875 20.1672 7.00163 19.853 7.38043 19.853H16.6196C16.9984 19.853 17.3125 20.1672 17.3125 20.546C17.3125 20.9248 16.9984 21.2389 16.6196 21.2389Z"
                fill="#DE5836"
              />
              <path
                d="M19.7146 6.01293L16.0189 8.65532C15.5293 9.00641 14.8271 8.79391 14.6146 8.23032L12.8684 3.5738C12.5727 2.77 11.4363 2.77 11.1407 3.5738L9.38523 8.22108C9.17273 8.79391 8.4798 9.00641 7.99012 8.64608L4.29447 6.00369C3.55534 5.4863 2.57599 6.21619 2.88088 7.07543L6.72436 17.839C6.85371 18.2086 7.2048 18.4488 7.59284 18.4488H16.3977C16.7858 18.4488 17.1369 18.1993 17.2662 17.839L21.1097 7.07543C21.4238 6.21619 20.4445 5.4863 19.7146 6.01293ZM14.3097 14.5406H9.69012C9.31132 14.5406 8.99719 14.2265 8.99719 13.8477C8.99719 13.4689 9.31132 13.1548 9.69012 13.1548H14.3097C14.6885 13.1548 15.0026 13.4689 15.0026 13.8477C15.0026 14.2265 14.6885 14.5406 14.3097 14.5406Z"
                fill="#DE5836"
              />
            </svg>
          </motion.button>

          {isSubscribed && (
            <motion.button
              className="relative h-[34px] w-[34px] bg-white rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-red-400 shadow-md"
              onClick={handleWishlistClick}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M16.102 3.77722C14.4298 3.77722 12.933 4.59027 11.9999 5.83755C11.0667 4.59027 9.56998 3.77722 7.8977 3.77722C5.06129 3.77722 2.76074 6.087 2.76074 8.9419C2.76074 10.0414 2.93629 11.0577 3.24118 12C4.70096 16.6196 9.20042 19.3821 11.427 20.1397C11.7412 20.2506 12.2586 20.2506 12.5727 20.1397C14.7993 19.3821 19.2988 16.6196 20.7586 12C21.0635 11.0577 21.239 10.0414 21.239 8.9419C21.239 6.087 18.9385 3.77722 16.102 3.77722Z"
                  fill={isWishlisted ? "#ED4A4A" : "#ccc"}
                />
              </svg>
            </motion.button>
          )}
        </div>
      </div>

      <div className="flex flex-col flex-1 p-4">
        <div className="flex-1 min-h-[3rem] mb-4">
          <h3 className="text-lg font-bold leading-6 text-black">{title}</h3>
        </div>

        {isSubscribed && (
          <div className="flex justify-between items-center mt-auto">
            <div className="flex justify-between items-center px-1.5 py-2.5 border border-solid border-black border-opacity-10 h-[50px] rounded-[90px] w-[129px]">
              <motion.button
                className="relative h-[37px] w-[37px] bg-gray-400 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  decrementQuantity();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Decrease quantity"
              >
                <svg width="25" height="25" viewBox="0 0 26 27" fill="none">
                  <path
                    d="M14.0458 14.5436H19.2765C19.8542 14.5436 20.3226 14.0752 20.3226 13.4974C20.3226 12.9197 19.8542 12.4513 19.2765 12.4513H14.0458H11.9535H6.72289C6.14513 12.4513 5.67676 12.9197 5.67676 13.4974C5.67676 14.0752 6.14513 14.5436 6.72289 14.5436H11.9535H14.0458Z"
                    fill="white"
                  />
                </svg>
              </motion.button>

              <span
                className="text-base font-bold leading-5 text-black"
                aria-live="polite"
              >
                {quantity}
              </span>

              <motion.button
                className="relative h-[37px] w-[37px] bg-gray-400 rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  incrementQuantity();
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Increase quantity"
              >
                <svg width="25" height="25" viewBox="0 0 26 27" fill="none">
                  <path
                    d="M20.3226 13.4979C20.3226 14.0757 19.8542 14.544 19.2765 14.544H14.0458V19.7747C14.0458 20.3525 13.5774 20.8208 12.9997 20.8208V20.8208C12.4219 20.8208 11.9535 20.3525 11.9535 19.7747V14.544H6.72289C6.14513 14.544 5.67676 14.0757 5.67676 13.4979V13.4979C5.67676 12.9201 6.14513 12.4518 6.72289 12.4518H11.9535V7.22112C11.9535 6.64336 12.4219 6.17499 12.9997 6.17499V6.17499C13.5774 6.17499 14.0458 6.64336 14.0458 7.22112V12.4518H19.2765C19.8542 12.4518 20.3226 12.9201 20.3226 13.4979V13.4979Z"
                    fill="white"
                  />
                </svg>
              </motion.button>
            </div>

            <motion.button
              className="relative h-[50px] w-[50px] bg-[#32B744] rounded-full flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-green-400"
              onClick={handleAddToCart}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Add to cart"
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
              >
                <path
                  d="M28.75 22.5C28.75 23.4375 28.4875 24.325 28.025 25.075C27.7625 25.525 27.425 25.925 27.0375 26.25C26.1625 27.0375 25.0125 27.5 23.75 27.5C22.225 27.5 20.8625 26.8125 19.9625 25.7375C19.9375 25.7 19.9 25.675 19.875 25.6375C19.725 25.4625 19.5875 25.275 19.475 25.075C19.0125 24.325 18.75 23.4375 18.75 22.5C18.75 20.925 19.475 19.5125 20.625 18.6C21.4875 17.9125 22.575 17.5 23.75 17.5C25 17.5 26.125 17.95 27 18.7125C27.15 18.825 27.2875 18.9625 27.4125 19.1C28.2375 20 28.75 21.1875 28.75 22.5Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M25.6127 22.475H21.8877"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M23.75 20.65V24.3875"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.9624 9.29999L14.9999 15.6875L25.9624 9.33745"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 27.0124V15.6749"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27.0123 11.4625V18.5375C27.0123 18.6 27.0123 18.65 26.9998 18.7125C26.1248 17.95 24.9998 17.5 23.7498 17.5C22.5748 17.5 21.4873 17.9125 20.6248 18.6C19.4748 19.5125 18.7498 20.925 18.7498 22.5C18.7498 23.4375 19.0123 24.325 19.4748 25.075C19.5873 25.275 19.7248 25.4625 19.8748 25.6375L17.5873 26.9C16.1623 27.7 13.8373 27.7 12.4123 26.9L5.7373 23.2C4.2248 22.3625 2.9873 20.2625 2.9873 18.5375V11.4625C2.9873 9.7375 4.2248 7.63753 5.7373 6.80003L12.4123 3.1C13.8373 2.3 16.1623 2.3 17.5873 3.1L24.2623 6.80003C25.7748 7.63753 27.0123 9.7375 27.0123 11.4625Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </motion.button>
          </div>
        )}
      </div>
    </motion.article>
  );
}

export default ProductCard;
